import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * @param {string} value
 * @returns {number}
 */
function parseTimeMs(value) {
  const t = value.trim();
  if (!t || t === '0s' || t === '0ms') return 0;
  if (t.endsWith('ms')) return parseFloat(t) || 0;
  if (t.endsWith('s')) return (parseFloat(t) || 0) * 1000;
  return 0;
}

/**
 * Durée max (ms) des transitions CSS sur l’élément (plusieurs propriétés = plusieurs valeurs).
 * @param {HTMLElement} el
 * @returns {number}
 */
function maxTransitionDurationMs(el) {
  const style = getComputedStyle(el);
  const durations = style.transitionDuration.split(',');
  const delays = style.transitionDelay.split(',');
  let max = 0;
  for (let i = 0; i < durations.length; i++) {
    const d = parseTimeMs(durations[i] || '0s');
    const delay = parseTimeMs(delays[i] || delays[0] || '0s');
    max = Math.max(max, d + delay);
  }
  return max;
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {() => void} */
  #abortDrawerTransitionWait = () => {};

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupMobileMegaTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#abortDrawerTransitionWait();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Après transition CSS `transform` sur le panneau (slide vertical / horizontal).
   * `getAnimations()` / animationEnd ne couvrent pas fiablement les transitions au bon moment.
   * @param {Element | null | undefined} panel
   * @param {() => void} callback
   */
  #afterDrawerPanelTransition(panel, callback) {
    this.#abortDrawerTransitionWait();

    if (!(panel instanceof HTMLElement)) {
      callback();
      return;
    }

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      requestAnimationFrame(callback);
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(timeoutId);
      this.#abortDrawerTransitionWait = () => {};
      callback();
    };

    /** @param {TransitionEvent} e */
    const onTransitionEnd = (e) => {
      if (e.target !== panel) return;
      if (e.propertyName !== 'transform') return;
      finish();
    };

    panel.addEventListener('transitionend', onTransitionEnd);

    const durationMs = maxTransitionDurationMs(panel);
    const timeoutId = setTimeout(finish, Math.max(durationMs + 120, 400));

    this.#abortDrawerTransitionWait = () => {
      settled = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(timeoutId);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (maxTransitionDurationMs(panel) === 0) {
          finish();
        }
      });
    });
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (this.isOpen) {
      // Sinon le <details> retire `open` tout de suite et le panneau disparaît sans transition de fermeture.
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      this.close();
      return;
    }
    this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      this.#afterDrawerPanelTransition(drawer, () => trapFocus(details));
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#abortDrawerTransitionWait();

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    this.#afterDrawerPanelTransition(drawer, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Barre de navigation parente mobile : scroll auto vers le lien actif à l'ouverture du drawer.
   */
  #setupMobileMegaTabs() {
    const mega = this.querySelector('.csk-mobile-mega');
    if (!mega) return;

    const parents = mega.querySelector('[data-csk-parents]');
    if (!parents) return;

    const scrollActiveIntoView = () => {
      const active = parents.querySelector('.is-active');
      if (!active) return;
      const links = Array.from(parents.querySelectorAll('.csk-mobile-mega__parent-link'));
      const isFirst = links.indexOf(active) === 0;
      active.scrollIntoView({ block: 'nearest', inline: isFirst ? 'start' : 'center', behavior: 'smooth' });
    };

    // Scroll au moment où le drawer devient visible
    const details = this.querySelector('details');
    if (details) {
      details.addEventListener('toggle', () => {
        if (details.open) requestAnimationFrame(scrollActiveIntoView);
      });
    }
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
